import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, MessageSquare, MessagesSquare } from "lucide-react";

import { Card } from "@/app/admin/_common/ui/card";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import { formatUtcDateTime } from "@/app/_utils/dayjs";
import { cn } from "@/app/_utils/cn";
import CommunicationsService, {
  Message,
} from "@/app/_services/CommunicationsService";

import {
  statusBadgeClasses,
  statusDotClasses,
  statusLabel,
} from "@/app/admin/tools/communications/_cmp/messageStatus";

const PREVIEW_COUNT = 10;

/**
 * What we've sent this guest.
 *
 * Same endpoint as the Communications feed, filtered to one guest — no bespoke backend.
 * Shows the most recent handful and links through to the full, filtered feed rather
 * than paging here.
 */
export default function GuestCommunicationsCard({ guestId }: { guestId: string }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    CommunicationsService.getMessagesAsync({ guest_id: guestId, limit: PREVIEW_COUNT })
      .then((page) => {
        if (!cancelled) setMessages(page.data);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [guestId]);

  return (
    <Card className="flex max-h-[320px] flex-col rounded-none border-0 px-2 py-4 shadow-none md:rounded-md md:border md:px-4">
      <div className="mb-5 flex flex-none items-center justify-between sm:mb-3">
        <span className="text-[16px] font-semibold text-foreground sm:text-[13.5px]">
          Messages
        </span>
        {messages.length > 0 && (
          <button
            type="button"
            className="text-[11.5px] text-muted-foreground hover:text-foreground hover:underline"
            onClick={() =>
              navigate(`/admin/tools/communications?guest_id=${guestId}`)
            }
          >
            View all
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="size-3.5 flex-none rounded" />
              <Skeleton className="h-3 flex-1" />
              <Skeleton className="h-4 w-16 flex-none rounded-[7px]" />
            </div>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CuoEmpty icon={MessagesSquare} text="Nothing sent yet" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {messages.map((message) => {
            const Icon = message.channel === "sms" ? MessageSquare : Mail;
            return (
              <div
                key={message.id}
                className="flex items-center gap-2.5 border-b border-border/50 py-2 last:border-0"
              >
                <Icon className="size-3.5 flex-none text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] text-foreground">
                    {message.subject || message.preview || "—"}
                  </div>
                  <div className="truncate text-[11.5px] text-muted-foreground">
                    {formatUtcDateTime(message.created_at)}
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "flex-none gap-1.5 rounded-[7px] font-normal",
                    statusBadgeClasses(message.status),
                  )}
                >
                  <span
                    className={cn(
                      "size-1.5 flex-none rounded-full",
                      statusDotClasses(message.status),
                    )}
                  />
                  {statusLabel(message.channel, message.status)}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
